import * as THREE from 'three';

// Selection utility functions extracted from STAADWorkspace3D

// Helper function to project 3D point to screen coordinates
export const projectToScreen = (
  worldPosition: THREE.Vector3, 
  camera: THREE.Camera, 
  canvasRect: DOMRect
): { x: number; y: number } => {
  const projected = worldPosition.clone().project(camera);
  
  // Convert from normalized device coordinates (-1 to 1) to screen coordinates
  const x = (projected.x + 1) * canvasRect.width / 2;
  const y = (-projected.y + 1) * canvasRect.height / 2;
  
  return { x, y };
};

// Helper function to check if line intersects rectangle
export const lineIntersectsRect = (
  x1: number, y1: number, x2: number, y2: number, 
  minX: number, maxX: number, minY: number, maxY: number
): boolean => {
  // Check if either endpoint is inside the rectangle
  if ((x1 >= minX && x1 <= maxX && y1 >= minY && y1 <= maxY) ||
      (x2 >= minX && x2 <= maxX && y2 >= minY && y2 <= maxY)) {
    return true;
  }
  
  // Check if line intersects any of the rectangle edges
  const edges = [
    [minX, minY, maxX, minY], // top
    [maxX, minY, maxX, maxY], // right
    [maxX, maxY, minX, maxY], // bottom
    [minX, maxY, minX, minY]  // left
  ];
  
  for (const [ex1, ey1, ex2, ey2] of edges) {
    if (linesIntersect(x1, y1, x2, y2, ex1, ey1, ex2, ey2)) {
      return true;
    }
  }
  
  return false;
};

// Helper function to check if two line segments intersect
export const linesIntersect = (
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number
): boolean => {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (denom === 0) return false;
  
  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;
  
  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
};

// Complete containment selection logic (STAAD.Pro style)
export const isCompletelyContained = (
  elementBounds: { left: number; right: number; top: number; bottom: number },
  selectionBounds: { minX: number; maxX: number; minY: number; maxY: number }
): boolean => {
  return (
    elementBounds.left >= selectionBounds.minX && 
    elementBounds.right <= selectionBounds.maxX && 
    elementBounds.top >= selectionBounds.minY && 
    elementBounds.bottom <= selectionBounds.maxY
  );
};
